import React, { useState, useCallback, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  applyNodeChanges,
  applyEdgeChanges,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { ResetProcedure } from '../../../Redux/ProcedureReducer';
import { api } from '../../../Api-Endpoints/apiEndpoint';
import Layout from '../../../Components/layout/Layout';
import { FaAngleLeft } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';

// Function to generate nodes
const generateNodes = (procedures = [], endProcedure) => {
  const nodes = [
    {
      id: 'start',
      type: 'input',
      data: { label: 'Start' },
      position: { x: 200, y: 100 },
      className: 'bg-green-100 border-green-500',
    },
  ];

  procedures.forEach((item, index) => {
    const isStep = item.steps?.title;

    nodes.push({
      id: item._id,
      data: {
        label: isStep ? (
          <div className="flex flex-col items-center">
            <strong>Step</strong>
            <span>{item.steps.title}</span>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <strong>Decision</strong>
            <span>{item.decisions.title}</span>
          </div>
        ),
      },
      position: { x: 200 + index * 50, y: 150 + index * 100 },
      className: isStep
        ? 'bg-blue-100 border-blue-500'
        : 'bg-yellow-100 border-yellow-500',
    });
  });

  nodes.push({
    id: 'end',
    data: {
      label: (
        <div className="flex flex-col items-center">
          <strong>End</strong>
          <span>{endProcedure?.title || 'End step'}</span>
        </div>
      ),
    },
    position: {
      x: procedures.length > 0 ? nodes[nodes.length - 1].position.x + 150 : 200,
      y: procedures.length > 0 ? nodes[nodes.length - 1].position.y + 150 : 200,
    },
    className: 'bg-red-100 border-red-500',
  });

  return nodes;
};

// Function to generate edges
const generateEdges = (procedures = []) => {
  const edges = [];

  if (procedures.length > 0) {
    edges.push({
      id: `start-${procedures[0]._id}`,
      source: 'start',
      target: procedures[0]._id,
      animated: true,
    });
  }

  procedures.forEach((item, index) => {
    const nextProcedure = procedures[index + 1];

    if (nextProcedure) {
      edges.push({
        id: `${item._id}-${nextProcedure._id}`,
        source: item._id,
        target: nextProcedure._id,
        animated: true,
      });
    }

    if (item.decisions?.answer) {
      item.decisions.answer.forEach(answer => {
        edges.push({
          id: `${item._id}-${answer._associate}`,
          source: item._id,
          target: answer._associate === 'endStep' ? 'end' : answer._associate,
          label: answer._ans,
          animated: true,
          style: { stroke: item.decisions ? '#EAB308' : '#4A90E2' },
        });
      });
    }
  });

  const endConnected = edges.some(edge => edge.target === 'end');
  if (!endConnected && procedures.length > 0) {
    const lastProcedure = procedures[procedures.length - 1];
    edges.push({
      id: `${lastProcedure._id}-end`,
      source: lastProcedure._id,
      target: 'end',
      animated: true,
    });
  }

  return edges;
};

const Flowchart = () => {
  const dispatch = useDispatch();
  const [flowNodes, setFlowNodes] = useState([]);
  const [flowEdges, setFlowEdges] = useState([]);
  const [procId, setProcId] = useState('');
  const navigate = useNavigate();

  const { procedure, isGotProcedure } = useSelector(
    state => ({
      procedure: state.Procedure.procedure,
      isGotProcedure: state.Procedure.isGotProcedure,
    }),
    shallowEqual
  );

  useEffect(() => {
    const fetchProcedureDetails = async () => {
      if (isGotProcedure) {
        updateFlow(procedure);
        dispatch(ResetProcedure());
      } else {
        const id = window.location.pathname.split('/').pop();
        setProcId(id);
        try {
          const { status, data } = await api.get(`procedure/by/${id}`);
          if (status === 200) {
            updateFlow(data[0]);
          }
        } catch (error) {
          console.error('Error fetching procedure details:', error);
        }
      }
    };

    fetchProcedureDetails();
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [isGotProcedure, procedure, dispatch]);

  const updateFlow = (procedureDetails = {}) => {
    const nodes = generateNodes(
      procedureDetails.procedures || [],
      procedureDetails?.endProcedure
    );
    const edges = generateEdges(
      procedureDetails.procedures || [],
      procedureDetails?.endProcedure
    );
    setFlowNodes(nodes);
    setFlowEdges(edges);
  };

  const onNodesChange = useCallback(
    changes => setFlowNodes(nds => applyNodeChanges(changes, nds)),
    []
  );

  const onEdgesChange = useCallback(
    changes => setFlowEdges(eds => applyEdgeChanges(changes, eds)),
    []
  );

  const onConnect = useCallback(
    params => setFlowEdges(eds => addEdge(params, eds)),
    []
  );

  const handleBack = () => {
    navigate(`/procedure-preview/${procId}`);
  };

  return (
    <Layout heading={'Flow chart'}>
      <button
        onClick={() => handleBack()}
        className="flex justify-center items-center space-x-2 bg-red-500 px-5 py-2 my-5 text-white rounded-lg font-semibold ml-[90%]"
      >
        <FaAngleLeft className="w-5 h-5" />
        <span>back</span>
      </button>
      <div className="w-full h-[80%] px-8 pb-50">
        <ReactFlow
          nodes={flowNodes}
          edges={flowEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          fitView
          className="bg-gray-50"
        >
          <Background />
          <Controls />
        </ReactFlow>
      </div>
    </Layout>
  );
};

export default Flowchart;
